import styled, { css } from 'styled-components';
import spacing from '../../../styles/spacing';
import { adaptiveGradientBorder, scrollShadow } from '../../../styles/mixins';
import { vars } from '../../../theme';
import { applyOpacity } from '../../../utils/color.utils';
import { IntentType } from '../HBKanban.types';

const BORDER_RADIUS = vars.radius.xl;

const getIntentSurfaceColor = (intent?: IntentType): string => {
    switch (intent) {
        case 'success':
            return vars.colors.surface.success;
        case 'warning':
            return vars.colors.surface.warning;
        case 'danger':
            return vars.colors.surface.danger;
        case 'upgrade':
            return vars.colors.surface.upgrade;
        case 'subtle':
        default:
            return applyOpacity(vars.colors.text.primary, 5);
    }
};

/** Stroke colors used by the adaptiveGradientBorder ::before (intent → fade to stroke.secondary). */
const getIntentStrokeColor = (intent?: IntentType): string => {
    switch (intent) {
        case 'success':
            return vars.colors.stroke.success;
        case 'warning':
            return vars.colors.stroke.warning;
        case 'danger':
            return vars.colors.stroke.danger;
        case 'upgrade':
            return vars.colors.stroke.upgrade;
        case 'subtle':
        default:
            return vars.colors.stroke.secondary;
    }
};

const getIntentGradient = (intent?: IntentType): string => {
    const strokeColor = getIntentStrokeColor(intent);
    // Painted onto adaptiveGradientBorder's ::before as a 1px masked border
    return `linear-gradient(180deg, ${strokeColor} 0%, ${vars.colors.stroke.secondary} 78px)`;
};

export const StyledColumn = styled.div<{
    $isOver?: boolean;
    $intent?: IntentType;
    $isEmpty?: boolean;
}>`
    display: flex;
    flex-direction: column;
    border-radius: ${BORDER_RADIUS};
    width: 280px;
    min-width: 280px;
    height: 100%;
    transition: all 0.1s ease;
    /* Stretch to match tallest column so all headers stick together */
    align-self: stretch;

    ${props =>
        props.$isOver &&
        css`
            overflow: visible;
        `}
`;

export const DashedBorder = styled.svg.attrs({
    'aria-hidden': true
})`
    position: absolute;
    inset: -0.5px;
    width: calc(100% + 1px);
    height: calc(100% + 1px);
    pointer-events: none;
    overflow: visible;
    transform: scale(1, -1);
`;

export const DashedBorderRect = styled.rect`
    width: 100%;
    height: 100%;
    rx: ${BORDER_RADIUS};
    ry: ${BORDER_RADIUS};
    fill: none;
    stroke: ${vars.colors.base.accent};
    stroke-width: 1px;
    stroke-dasharray: 4 4;
    stroke-linecap: butt;
    vector-effect: non-scaling-stroke;
`;

export const StyledColumnContent = styled.div<{
    $isOver?: boolean;
    $isOverNonDroppable?: boolean;
    $intent?: IntentType;
    $isEmpty?: boolean;
    $maxHeight?: number;
}>`
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0 3px;
    transition: all 0.1s ease;
    background: ${props => getIntentSurfaceColor(props.$intent)};
    border-radius: ${BORDER_RADIUS};

    border: 1px solid transparent;
    user-select: none;
    position: relative;

    ${props => {
        const intent = props.$intent;
        const strokeColor = getIntentStrokeColor(intent);
        const gradient = getIntentGradient(intent);

        // Don't apply gradient border when dragging over (to show dashed border or non-droppable state)
        if (
            strokeColor !== 'transparent' &&
            !(props.$isOver || props.$isOverNonDroppable)
        ) {
            return css`
                ${adaptiveGradientBorder(
                    css`
                        ${gradient}
                    `,
                    BORDER_RADIUS,
                    '1px',
                    '-1px'
                )}
            `;
        }

        // When a column is non-droppable, cover the items with a pattern which reflects that
        if (props.$isOverNonDroppable) {
            return css`
                border-color: ${vars.colors.surface.itemSelectedHover};

                &::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: ${BORDER_RADIUS};
                    z-index: 900; // high to avoid being covered by card contents but below 999 which is the DragOverlay's z-index

                    // the following is generated by https://stripesgenerator.com/stripe/10968
                    background: linear-gradient(
                            135deg,
                            ${vars.colors.surface.itemSelectedHover} 7.14%,
                            ${vars.colors.surface.container} 7.14%,
                            ${vars.colors.surface.container} 50%,
                            ${vars.colors.surface.itemSelectedHover} 50%,
                            ${vars.colors.surface.itemSelectedHover} 57.14%,
                            ${vars.colors.surface.container} 57.14%,
                            ${vars.colors.surface.container} 100%
                        )
                        repeat;
                    background-size: 9.9px 9.9px;

                    background-position: 4px 4px;
                }
            `;
        }
    }}
`;

export const LoaderContainer = styled.div`
    position: relative;
    height: 100%;
    // create a new stacking context to prevent loader from covering column headers
    isolation: isolate;
`;

export const StyledColumnItemsWrapper = styled.div<{
    $isScrolledTop: boolean;
    $isScrolledBottom: boolean;
    $intent?: IntentType;
}>`
    flex: 1 0 auto;
    min-height: 0;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: ${vars.radius.xl};

    ${({ $isScrolledTop, $intent }) => {
        const surfaceColor = getIntentSurfaceColor($intent);
        return scrollShadow({
            isShadowActive: $isScrolledTop,
            size: 20,
            direction: 'top',
            isInset: true,
            isFlat: true,
            borderRadius: 3,
            flatColor: surfaceColor
        });
    }}
    ${({ $isScrolledBottom, $intent }) => {
        const surfaceColor = getIntentSurfaceColor($intent);
        return scrollShadow({
            isShadowActive: !$isScrolledBottom,
            size: 40,
            direction: 'bottom',
            isInset: true,
            isFlat: true,
            borderRadius: 3,
            flatColor: surfaceColor
        });
    }}
`;

export const StyledColumnItems = styled.div`
    height: 100%;
    max-height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    gap: ${spacing[12]};
    border-radius: ${vars.radius.xl};
    padding: 7px ${spacing[4]};
`;
